import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileDiff, FileText, Loader2, Mail, Paperclip, Plus, Receipt, RotateCcw, Send, Trash2, Undo2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { usePermissions } from "@/lib/permissions";
import { confirmAction } from "@/lib/dialogs";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const signed = (v) => `${Number(v) > 0 ? "+" : ""}${inr(v)}`;
const NO_WBS = "__none__";
const EMPTY_LINE = { wbs_item_id: "", description: "", unit: "", quantity_change: "", rate_cost: "", rate_price: "" };
const EMPTY = { title: "", reason: "", requested_by: "", client_ref: "", time_impact_days: "0", lines: [{ ...EMPTY_LINE }] };
const STATUS_HELP = {
  DRAFT: "Being written. It can still be changed or cancelled.",
  SUBMITTED: "Waiting for approval. Nothing changes in the contract value until it is approved.",
  APPROVED: "Approved: it is in the revised contract value and the forecast. It can't be edited; reverse it to correct it.",
  REJECTED: "Rejected. Work on it must not go ahead.",
  CLIENT_ACCEPTED: "Accepted by the client: its work can be billed on an RA bill.",
  CANCELLED: "Cancelled before approval.",
};

function Tile({ label, value, sub, strong, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-lg tabular-nums ${strong ? "font-semibold text-[#910029] dark:text-rose-300" : "font-semibold text-slate-900 dark:text-slate-50"}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

/** Project → Variations: client-requested changes. Original contract stays visible; approved variations make the revised value. */
export default function Variations() {
  const { projectId } = useProjectCtx();
  const { can } = usePermissions();
  const canCreate = can("VARIATIONS", "create");
  const canApprove = can("VARIATIONS", "approve");
  const canBill = can("RA_BILLS", "create");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [wbs, setWbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);      // null | "new" | variation
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }, { data: items }] = await Promise.all([
        api.get(`/projects/${projectId}/variations`),
        api.get(`/projects/${projectId}/variations/summary`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }).catch(() => ({ data: [] })),
      ]);
      setRows(list); setSummary(sum); setWbs(items || []);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const open = rows.find((r) => r.id === openId) || null;
  const wbsName = useMemo(() => Object.fromEntries(wbs.map((w) => [w.id, `${w.code} — ${w.name}`])), [wbs]);

  return (
    <div className="space-y-5" data-testid="variations-page">
      <PageHeader title="Variations" description="Changes the client asks for. The original contract stays as signed; approved variations make the revised contract value, forecast and budget."
        action={<div className="flex gap-2">
          <Button variant="outline" asChild><Link to={`/erp/delivery/variation-register?project_id=${projectId}`} data-testid="variation-register-link">Register</Link></Button>
          {canCreate && <Button onClick={() => setEditing("new")} data-testid="new-variation"><Plus className="h-4 w-4" /> New variation</Button>}
        </div>} />

      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="variation-summary">
          <Tile label="Original contract" value={inr(summary.original_contract_value)} sub="As signed" testId="sum-original" />
          <Tile label="Approved variations" value={signed(summary.approved_variations)} sub={`Cost ${signed(summary.approved_cost_impact)} · ${summary.time_impact_days} day(s)`} testId="sum-approved" />
          <Tile label="Pending approval" value={signed(summary.pending_variations)} sub={Number(summary.draft_variations) ? `Drafts ${signed(summary.draft_variations)}` : "Not in the contract yet"} testId="sum-pending" />
          <Tile label="Revised contract value" value={inr(summary.revised_contract_value)} sub="Original + approved" strong testId="sum-revised" />
        </div>
      )}

      {loading && !rows.length ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        : !rows.length ? <EmptyState title="No variations yet" description={canCreate ? "When the client asks for a change, raise it here before the work starts." : "Variations raised on this project appear here."} testId="variations-empty" />
          : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <Table>
                <TableHeader><TableRow className="bg-[#ECF4F7] dark:bg-slate-800">
                  <TableHead>Number</TableHead><TableHead>Title</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Price</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Days</TableHead><TableHead>Raised</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((v) => (
                    <TableRow key={v.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setOpenId(v.id)} data-testid={`variation-row-${v.number}`}>
                      <TableCell className="font-medium">{v.number}{v.reverses_id && <span className="ml-1 text-xs text-muted-foreground">(reversal)</span>}</TableCell>
                      <TableCell className="max-w-xs truncate">{v.title}</TableCell>
                      <TableCell><StatusPill value={v.status} /></TableCell>
                      <TableCell className="text-right tabular-nums">{signed(v.price_impact)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{signed(v.cost_impact)}</TableCell>
                      <TableCell className="text-right tabular-nums">{v.time_impact_days || 0}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(v.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      {editing && <VariationForm projectId={projectId} wbs={wbs} variation={editing === "new" ? null : editing}
        onClose={() => setEditing(null)} onSaved={(v) => { setEditing(null); load(); setOpenId(v.id); }} />}
      {open && <VariationDetail projectId={projectId} v={open} wbsName={wbsName} canCreate={canCreate} canApprove={canApprove} canBill={canBill}
        onClose={() => setOpenId(null)} onEdit={() => { setEditing(open); setOpenId(null); }} onChanged={(next) => { load(); if (next) setOpenId(next); }} />}
    </div>
  );
}

function VariationForm({ projectId, wbs, variation, onClose, onSaved }) {
  const [form, setForm] = useState(() => (variation ? {
    title: variation.title, reason: variation.reason || "", requested_by: variation.requested_by || "", client_ref: variation.client_ref || "",
    time_impact_days: String(variation.time_impact_days || 0),
    lines: variation.lines.map((l) => ({ wbs_item_id: l.wbs_item_id || "", description: l.description, unit: l.unit || "", quantity_change: l.quantity_change, rate_cost: l.rate_cost, rate_price: l.rate_price })),
  } : EMPTY));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const totals = form.lines.reduce((t, l) => ({
    cost: t.cost + Number(l.quantity_change || 0) * Number(l.rate_cost || 0), price: t.price + Number(l.quantity_change || 0) * Number(l.rate_price || 0),
  }), { cost: 0, price: 0 });
  const valid = form.title.trim() && form.lines.length && form.lines.every((l) => l.description.trim() && Number(l.quantity_change) !== 0);

  const save = async () => {
    setSaving(true);
    const body = { title: form.title.trim(), reason: form.reason || null, requested_by: form.requested_by || null, client_ref: form.client_ref || null,
      time_impact_days: Number(form.time_impact_days || 0),
      lines: form.lines.map((l) => ({ wbs_item_id: l.wbs_item_id || null, description: l.description.trim(), unit: l.unit || null,
        quantity_change: l.quantity_change, rate_cost: l.rate_cost || "0", rate_price: l.rate_price || "0" })) };
    try {
      const { data } = variation ? await api.patch(`/projects/${projectId}/variations/${variation.id}`, body) : await api.post(`/projects/${projectId}/variations`, body);
      toast.success(variation ? "Variation saved" : `${data.number} raised as a draft`);
      onSaved(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="variation-form">
        <DialogHeader>
          <DialogTitle>{variation ? `Edit ${variation.number}` : "New variation"}</DialogTitle>
          <DialogDescription>Price and cost are worked out from the lines. Use a negative quantity for work that is left out.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2"><Label>Title <span className="text-rose-600">*</span></Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="variation-title" /></div>
          <div className="md:col-span-2"><Label>Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} /></div>
          <div><Label>Requested by (client)</Label><Input value={form.requested_by} onChange={(e) => set("requested_by", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Client reference</Label><Input value={form.client_ref} onChange={(e) => set("client_ref", e.target.value)} /></div>
            <div><Label>Time impact (days)</Label><Input type="number" value={form.time_impact_days} onChange={(e) => set("time_impact_days", e.target.value)} /></div>
          </div>
        </div>
        <div className="mt-2 overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-[#ECF4F7] text-left text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"><tr>
              <th className="px-2 py-2">Activity</th><th className="px-2 py-2">Description *</th><th className="w-20 px-2 py-2">Unit</th>
              <th className="w-24 px-2 py-2">Qty change *</th><th className="w-24 px-2 py-2">Cost rate</th><th className="w-24 px-2 py-2">Price rate</th><th className="w-28 px-2 py-2 text-right">Price</th><th className="w-8" />
            </tr></thead>
            <tbody>
              {form.lines.map((l, i) => (
                <tr key={i} className="border-t" data-testid={`variation-line-${i}`}>
                  <td className="px-2 py-1.5">
                    <Select value={l.wbs_item_id || NO_WBS} onValueChange={(v) => setLine(i, "wbs_item_id", v === NO_WBS ? "" : v)}>
                      <SelectTrigger className="h-8 w-48"><SelectValue placeholder="Activity" /></SelectTrigger>
                      <SelectContent><SelectItem value={NO_WBS}>No activity</SelectItem>{wbs.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5"><Input className="h-8" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} aria-label="Description" data-testid={`line-desc-${i}`} /></td>
                  <td className="px-2 py-1.5"><Input className="h-8" value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} aria-label="Unit" /></td>
                  <td className="px-2 py-1.5"><Input className="h-8" type="number" step="any" value={l.quantity_change} onChange={(e) => setLine(i, "quantity_change", e.target.value)} aria-label="Quantity change" data-testid={`line-qty-${i}`} /></td>
                  <td className="px-2 py-1.5"><Input className="h-8" type="number" step="any" min="0" value={l.rate_cost} onChange={(e) => setLine(i, "rate_cost", e.target.value)} aria-label="Cost rate" /></td>
                  <td className="px-2 py-1.5"><Input className="h-8" type="number" step="any" min="0" value={l.rate_price} onChange={(e) => setLine(i, "rate_price", e.target.value)} aria-label="Price rate" data-testid={`line-price-${i}`} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{signed(Number(l.quantity_change || 0) * Number(l.rate_price || 0))}</td>
                  <td className="px-1">{form.lines.length > 1 && <button type="button" aria-label="Remove line" onClick={() => set("lines", form.lines.filter((_, j) => j !== i))} className="rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t bg-slate-50 text-sm dark:bg-slate-800/50">
              <td colSpan={6} className="px-2 py-2"><Button type="button" variant="ghost" size="sm" onClick={() => set("lines", [...form.lines, { ...EMPTY_LINE }])}><Plus className="h-3.5 w-3.5" /> Add line</Button></td>
              <td className="px-2 py-2 text-right font-semibold tabular-nums" data-testid="form-price-total">{signed(totals.price)}</td><td />
            </tr></tfoot>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">Cost impact {signed(totals.cost)} · margin on this variation {signed(totals.price - totals.cost)}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!valid || saving} data-testid="variation-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VariationDetail({ projectId, v, wbsName, canCreate, canApprove, canBill, onClose, onEdit, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [billing, setBilling] = useState(false);
  const act = async (action, body, ok) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/projects/${projectId}/variations/${v.id}/${action}`, body || undefined);
      toast.success(ok(data));
      onChanged(action === "reverse" ? data.id : null);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const submit = async () => {
    setBusy("submit");
    try {
      const { data } = await api.post(`/projects/${projectId}/variations/${v.id}/submit`, { email_client: true });
      toast.success(data.status === "APPROVED" ? `${data.number} approved` : `${data.number} sent for approval`);
      const mail = data.client_email;
      if (mail?.status === "queued") toast.success(`Variation order emailed to the client (${mail.to})`);
      else if (mail?.status === "skipped") toast.warning(`Not emailed to the client: ${mail.reason}`);
      onChanged(null);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const openPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/projects/${projectId}/variations/${v.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const emailClient = async () => {
    if (!(await confirmAction({ title: `Email ${v.number} to the client?`, description: "The variation order goes as a PDF to the client's saved email address. Replies come to you.", confirmLabel: "Send email" }))) return;
    setBusy("email");
    try {
      const { data } = await api.post(`/projects/${projectId}/variations/${v.id}/email`);
      toast.success(`Emailed to ${data.sent_to}`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const decide = async (decision) => {
    if (decision === "reject" && !(await confirmAction({ title: `Reject ${v.number}?`, description: "The work on it must not go ahead. This can't be undone.", confirmLabel: "Reject", kind: "danger" }))) return;
    act(decision, {}, (d) => `${d.number} ${d.status === "APPROVED" ? "approved" : "rejected"}`);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="variation-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileDiff className="h-5 w-5 text-[#910029]" /> {v.number} · {v.title} <StatusPill value={v.status} /></DialogTitle>
          <DialogDescription>{STATUS_HELP[v.status]}</DialogDescription>
        </DialogHeader>
        {v.billing_warning && (
          <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200" data-testid="variation-billing-warning">
            {v.billing_warning}
          </div>
        )}
        <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <Field label="Price impact" value={signed(v.price_impact)} />
          <Field label="Cost impact" value={signed(v.cost_impact)} />
          <Field label="Time impact" value={`${v.time_impact_days || 0} day(s)`} />
          <Field label="Requested by" value={v.requested_by || "—"} />
          <Field label="Client reference" value={v.client_ref || "—"} />
          <Field label="Approved" value={v.approved_at ? fmtDate(v.approved_at) : "—"} />
          {Number(v.billed_amount) > 0 && <Field label="Billed on RA bills" value={inr(v.billed_amount)} />}
          {v.client_accepted_at && <Field label="Accepted by client" value={fmtDate(v.client_accepted_at)} />}
          {v.reason && <div className="sm:col-span-2"><Field label="Reason" value={v.reason} /></div>}
        </div>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-[#ECF4F7] text-left text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"><tr>
              <th className="px-3 py-2">Activity</th><th className="px-3 py-2">Description</th><th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Cost rate</th><th className="px-3 py-2 text-right">Price rate</th><th className="px-3 py-2 text-right">Price</th>
            </tr></thead>
            <tbody>{v.lines.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="px-3 py-1.5 text-muted-foreground">{l.boq_code && <span className="mr-1 text-xs font-medium text-[#910029]">{l.boq_code}</span>}{l.wbs_item_id ? (wbsName[l.wbs_item_id] || "Activity") : "—"}</td>
                <td className="px-3 py-1.5">{l.description}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{Number(l.quantity_change)} {l.unit || ""}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{inr(l.rate_cost)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{inr(l.rate_price)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{signed(l.price_amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <Documents projectId={projectId} v={v} />
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!!busy} onClick={openPdf} data-testid="variation-pdf">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF</Button>
            {["SUBMITTED", "APPROVED", "CLIENT_ACCEPTED"].includes(v.status) && canCreate && (
              <Button variant="outline" disabled={!!busy} onClick={emailClient} data-testid="variation-email">{busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email to client</Button>
            )}
            {v.status === "DRAFT" && canCreate && <Button variant="outline" onClick={onEdit} data-testid="variation-edit">Edit</Button>}
            {["DRAFT", "SUBMITTED"].includes(v.status) && canCreate && (
              <Button variant="ghost" disabled={!!busy} data-testid="variation-cancel" onClick={async () => (await confirmAction({ title: `Cancel ${v.number}?`, description: "It will not go to approval.", confirmLabel: "Cancel variation", kind: "danger" })) && act("cancel", {}, (d) => `${d.number} cancelled`)}>
                <XCircle className="h-4 w-4" /> Cancel variation
              </Button>
            )}
            {["APPROVED", "CLIENT_ACCEPTED"].includes(v.status) && canCreate && !v.reverses_id && (
              <Button variant="ghost" disabled={!!busy} data-testid="variation-reverse" onClick={async () => (await confirmAction({ title: `Reverse ${v.number}?`, description: `This raises a new draft with the opposite quantities. Approve it to take the change out of the contract.${Number(v.billed_amount) > 0 ? ` Note: ${inr(v.billed_amount)} of it is already billed on RA bills; that stays billed and has to be credited on a later RA bill.` : ""}`, confirmLabel: "Raise reversal" })) && act("reverse", {}, (d) => `${d.number} raised to reverse ${v.number}`)}>
                <RotateCcw className="h-4 w-4" /> Reverse
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {v.status === "DRAFT" && canApprove && <Button disabled={!!busy} onClick={submit} title="Sends it for approval and emails the variation order to the client" data-testid="variation-submit">{busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit</Button>}
            {v.status === "SUBMITTED" && canApprove && <>
              <Button variant="outline" disabled={!!busy} onClick={() => decide("reject")} data-testid="variation-reject"><Undo2 className="h-4 w-4" /> Reject</Button>
              <Button disabled={!!busy} onClick={() => decide("approve")} data-testid="variation-approve"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
            </>}
            {v.status === "APPROVED" && canApprove && <Button variant="outline" onClick={() => setAccepting(true)} data-testid="variation-client-accept"><CheckCircle2 className="h-4 w-4" /> Client accepted</Button>}
            {["APPROVED", "CLIENT_ACCEPTED"].includes(v.status) && canBill && Number(v.price_impact) > 0 && <Button variant="outline" onClick={() => setBilling(true)} data-testid="variation-bill"><Receipt className="h-4 w-4" /> Bill on RA bill</Button>}
          </div>
        </DialogFooter>
        {accepting && <ClientAccept onClose={() => setAccepting(false)} onSave={(body) => { setAccepting(false); act("client-accept", body, (d) => `${d.number} accepted by the client`); }} defaultRef={v.client_ref} />}
        {billing && <BillOnRABill projectId={projectId} v={v} onClose={() => setBilling(false)} onDone={() => { setBilling(false); onChanged(null); }} />}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return <div className="flex gap-2 py-0.5"><span className="w-36 shrink-0 text-muted-foreground">{label}</span><span className="text-slate-800 dark:text-slate-100">{value}</span></div>;
}

function ClientAccept({ onClose, onSave, defaultRef }) {
  const [on, setOn] = useState(todayISO());
  const [ref, setRef] = useState(defaultRef || "");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="client-accept-dialog">
        <DialogHeader><DialogTitle>Client acceptance</DialogTitle><DialogDescription>Record when the client accepted the variation and their reference. After this its work can be billed.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Accepted on</Label><Input type="date" value={on} onChange={(e) => setOn(e.target.value)} /></div>
          <div><Label>Client reference</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. their letter or PO amendment number" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={!on} onClick={() => onSave({ accepted_on: on, client_ref: ref || null })}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Put this variation's work on a draft RA bill. The server refuses until the variation is billable and caps the quantity. */
function BillOnRABill({ projectId, v, onClose, onDone }) {
  const [bills, setBills] = useState(null);
  const [lines, setLines] = useState([]);
  const [billId, setBillId] = useState("");
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/revenue/ra-bills"), api.get(`/projects/${projectId}/variations/billable-lines`)])
      .then(([{ data: b }, { data: l }]) => {
        const drafts = (b || []).filter((x) => x.project_id === projectId && x.status === "DRAFT");
        setBills(drafts); setBillId(drafts[0]?.id || ""); setLines((l || []).filter((x) => x.variation_id === v.id));
      })
      .catch((err) => { toast.error(getErrorMessage(err)); setBills([]); });
  }, [projectId, v.id]);
  const save = async () => {
    setSaving(true);
    try {
      for (const l of lines) {
        if (Number(qty[l.variation_line_id] || 0) > 0) {
          await api.post(`/revenue/ra-bills/${billId}/variation-lines`, { variation_line_id: l.variation_line_id, quantity: qty[l.variation_line_id] });
        }
      }
      toast.success("Added to the RA bill");
      onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  const any = lines.some((l) => Number(qty[l.variation_line_id] || 0) > 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bill-dialog">
        <DialogHeader><DialogTitle>Bill {v.number} on an RA bill</DialogTitle>
          <DialogDescription>Variation work goes on a draft RA bill at the variation's price. Remember to include it in the bill's gross amount.</DialogDescription></DialogHeader>
        {bills === null ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          : !lines.length ? <p className="text-sm text-muted-foreground" data-testid="not-billable">Nothing to bill yet: the variation must be accepted by the client first (or approved, if your organisation doesn't need client acceptance), or everything is already billed.</p>
            : !bills.length ? <p className="text-sm text-muted-foreground">There is no draft RA bill on this project. Create one under Client Management → RA Bills, then come back.</p>
              : (
                <div className="space-y-3">
                  <div><Label>Draft RA bill</Label>
                    <Select value={billId} onValueChange={setBillId}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{bills.map((b) => <SelectItem key={b.id} value={b.id}>{b.number} · {fmtDate(b.bill_date)}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <table className="w-full text-sm"><thead className="text-left text-xs text-muted-foreground"><tr><th className="py-1">Line</th><th className="py-1 text-right">Left to bill</th><th className="w-28 py-1">Bill now</th><th className="py-1 text-right">Amount</th></tr></thead>
                    <tbody>{lines.map((l) => (
                      <tr key={l.variation_line_id} className="border-t">
                        <td className="py-1.5">{l.description}</td>
                        <td className="py-1.5 text-right tabular-nums">{Number(l.left)} {l.unit || ""}</td>
                        <td className="py-1.5"><Input className="h-8" type="number" min="0" max={l.left} step="any" value={qty[l.variation_line_id] || ""} onChange={(e) => setQty((q) => ({ ...q, [l.variation_line_id]: e.target.value }))} aria-label={`Quantity for ${l.description}`} /></td>
                        <td className="py-1.5 text-right tabular-nums">{inr(Number(qty[l.variation_line_id] || 0) * Number(l.rate))}</td>
                      </tr>
                    ))}</tbody></table>
                </div>
              )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button>{!!lines.length && !!bills?.length && <Button disabled={!any || saving || !billId} onClick={save} data-testid="bill-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add to bill</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Documents for this variation live with the project's documents (folder "Variations", tagged with the variation number). */
function Documents({ projectId, v }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => {
    api.get(`/projects/${projectId}/documents`, { params: { folder: "Variations", q: v.number } })
      .then(({ data }) => setDocs((data.items || data || []).filter((d) => d.doc_number === v.number)))
      .catch(() => setDocs([]));
  }, [projectId, v.number]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("folder", "Variations"); fd.append("doc_number", v.number); fd.append("title", `${v.number} · ${file.name}`);
    try { await api.post(`/projects/${projectId}/documents`, fd); toast.success("Attached"); load(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setUploading(false); }
  };
  return (
    <div className="rounded-lg border p-3" data-testid="variation-documents">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Documents</div>
        <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-[#910029] hover:underline dark:text-rose-300">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Attach
          <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="variation-attach" />
        </label>
      </div>
      {docs === null ? <p className="mt-1 text-xs text-muted-foreground">Loading…</p>
        : !docs.length ? <p className="mt-1 text-xs text-muted-foreground">No documents yet: attach the client's instruction, drawings or the signed variation order.</p>
          : <ul className="mt-1 space-y-1 text-sm">{docs.map((d) => (
            <li key={d.id}><a href={`/api/v1/projects/${projectId}/documents/${d.id}/file`} onClick={async (e) => {
              e.preventDefault();
              try { const res = await api.get(`/projects/${projectId}/documents/${d.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); } catch (err) { toast.error(getErrorMessage(err)); }
            }} className="text-[#910029] hover:underline dark:text-rose-300">{d.title || d.filename}</a> <span className="text-xs text-muted-foreground">· {fmtDate(d.created_at)}</span></li>
          ))}</ul>}
    </div>
  );
}
